import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useBrandingSettings, useAppSettings } from '@/contexts/AppSettingsContext';
import { getBaseUrl } from '@/lib/seoDefaults';

interface SEOMetaTagsProps {
  viewport?: string;
  themeColor?: string;
  appleMobileWebAppCapable?: boolean;
  appleMobileWebAppStatusBarStyle?: 'default' | 'black' | 'black-translucent';
  appleMobileWebAppTitle?: string;
  appleTouchIcon?: string;
  favicon?: string;
  preconnect?: (string | { href: string; crossOrigin?: 'anonymous' | 'use-credentials' | '' | false })[];
  dnsPrefetch?: string[];
}

const SEOMetaTags: React.FC<SEOMetaTagsProps> = ({
  viewport = 'width=device-width, initial-scale=1.0, viewport-fit=cover',
  themeColor = '#3b82f6',
  appleMobileWebAppCapable = true,
  appleMobileWebAppStatusBarStyle = 'default',
  appleMobileWebAppTitle,
  appleTouchIcon,
  favicon,
  preconnect = [],
  dnsPrefetch = []
}) => {
  const { settings } = useBrandingSettings();
  const { settings: appSettings } = useAppSettings();
  
  // Use dynamic settings or fallback to props
  const dynamicThemeColor = settings?.pwa_theme_color || settings?.primary_color || themeColor;
  const dynamicAppTitle = settings?.pwa_short_name || settings?.app_name || appleMobileWebAppTitle;
  const dynamicFavicon = settings?.favicon_url || favicon;

  // Get current URL for hreflang
  const baseUrl = getBaseUrl();
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const currentSearch = typeof window !== 'undefined' ? window.location.search : '';
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const fullCurrentUrl = currentUrl || `${baseUrl}${currentPath}${currentSearch}`;
  
  // Get current language for self-referencing hreflang
  const currentLanguage = appSettings?.seo_content_language || (typeof window !== 'undefined' ? document.documentElement.lang || 'ro' : 'ro');
  
  return (
    <Helmet>
      {/* Google Search Console Verification */}
      {settings?.google_site_verification && (
        <meta name="google-site-verification" content={settings.google_site_verification} />
      )}
      
      {/* Extended SEO Meta Tags */}
      {appSettings?.seo_meta_generator && (
        <meta name="generator" content={appSettings.seo_meta_generator} />
      )}
      
      {appSettings?.seo_content_language && (
        <meta httpEquiv="Content-Language" content={appSettings.seo_content_language} />
      )}
      
      {appSettings?.seo_meta_referrer && (
        <meta name="referrer" content={appSettings.seo_meta_referrer} />
      )}
      
      {/* Hreflang tags for international SEO */}
      {appSettings?.seo_hreflang_languages && appSettings.seo_hreflang_languages.length > 0 && (
        <>
          {/* Self-referencing hreflang for current language */}
          <link rel="alternate" hreflang={currentLanguage} href={fullCurrentUrl} />
          
          {/* Alternate language versions */}
          {appSettings.seo_hreflang_languages
            .filter(langConfig => langConfig.lang && langConfig.lang.trim() && langConfig.url && langConfig.url.trim())
            .map((langConfig, index) => {
              // Build absolute URL for hreflang
              let hreflangUrl = langConfig.url.trim();
              
              // If URL is absolute, use it as is
              if (hreflangUrl.startsWith('http://') || hreflangUrl.startsWith('https://')) {
                // URL is already absolute, but we need to ensure it points to the same page
                // If it's just a base URL, append current path
                try {
                  const urlObj = new URL(hreflangUrl);
                  // If URL has no path or just root, use current path
                  if (!urlObj.pathname || urlObj.pathname === '/') {
                    hreflangUrl = `${urlObj.origin}${currentPath}${currentSearch}`;
                  } else {
                    // Use the configured path but ensure it's complete
                    hreflangUrl = `${urlObj.origin}${urlObj.pathname}${urlObj.search || currentSearch}`;
                  }
                } catch (e) {
                  // Invalid URL, fallback to baseUrl + currentPath
                  hreflangUrl = `${baseUrl}${currentPath}${currentSearch}`;
                }
              } else {
                // Relative URL - make it absolute
                const path = hreflangUrl.startsWith('/') ? hreflangUrl : `/${hreflangUrl}`;
                hreflangUrl = `${baseUrl}${path}${currentSearch}`;
              }
              
              return (
                <link key={`hreflang-${langConfig.lang.trim()}-${index}`} rel="alternate" hreflang={langConfig.lang.trim()} href={hreflangUrl} />
              );
            })}
          {/* Add x-default if not already present */}
          {!appSettings.seo_hreflang_languages.some(lang => lang.lang && lang.lang.trim() === 'x-default') && (
            <link rel="alternate" hreflang="x-default" href={fullCurrentUrl} />
          )}
        </>
      )}
      
      {/* Viewport and Mobile Optimization */}
      <meta name="viewport" content={viewport} />
      <meta name="theme-color" content={dynamicThemeColor} />
      <meta name="msapplication-TileColor" content={dynamicThemeColor} />
      <meta name="msapplication-config" content="/browserconfig.xml" />
      
      {/* Apple Mobile Web App */}
      <meta name="apple-mobile-web-app-capable" content={appleMobileWebAppCapable ? 'yes' : 'no'} />
      <meta name="apple-mobile-web-app-status-bar-style" content={appleMobileWebAppStatusBarStyle} />
      {dynamicAppTitle && <meta name="apple-mobile-web-app-title" content={dynamicAppTitle} />}
      
      {/* Icons */}
      {/* Note: Dynamic favicon is handled by DynamicFavicon component */}
      {/* Only add static fallback if no dynamic favicon is available */}
      {!dynamicFavicon && (
        <>
          <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
          <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
          <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />
        </>
      )}
      {/* Apple touch icon fallback if not provided via dynamic favicon */}
      {!dynamicFavicon && appleTouchIcon && <link rel="apple-touch-icon" href={appleTouchIcon} />}

      {/* Performance Optimization — crossOrigin matches former index.html preconnect for CORS-capable origins */}
      {preconnect.map((item, index) => {
        const isObject = typeof item === 'object' && item !== null;
        const href = isObject ? item.href : item;
        const crossOriginVal = isObject ? item.crossOrigin : undefined;
        
        return crossOriginVal === false ? (
          <link key={`preconnect-${index}`} rel="preconnect" href={href} />
        ) : (
          <link key={`preconnect-${index}`} rel="preconnect" href={href} crossOrigin={crossOriginVal} />
        );
      })}
      {dnsPrefetch.map((url, index) => (
        <link key={`dns-prefetch-${index}`} rel="dns-prefetch" href={url} />
      ))}
      
      {/* Security */}
      {/* Note: X-Frame-Options, CSP, HSTS, COOP must be set via HTTP headers, not meta tags */}
      {/* These should be configured in Cloudflare Pages Transform Rules or Workers */}
      <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
      <meta httpEquiv="X-XSS-Protection" content="1; mode=block" />
      <meta httpEquiv="Referrer-Policy" content="strict-origin-when-cross-origin" />
      
      {/* Additional SEO */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      {dynamicAppTitle && <meta name="application-name" content={dynamicAppTitle} />}
      
      {/* Cache Control */}
      <meta httpEquiv="Cache-Control" content="public, max-age=31536000" />
    </Helmet>
  );
};

export default SEOMetaTags;
